
import { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import * as Yup from 'yup';
import axios from "axios";

import { Formik, Form, ErrorMessage, Field, validateYupSchema } from "formik";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminAPI } from "@/services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Edit, Trash2, UserX, ListChecks } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";
import config from "../../config/config";
import { RotateCcw } from 'lucide-react';

const API_URL = config.API_URL;

const ManageAdmins = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRetoreModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const phoneRex = /^\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    mobileNumber: "",
    isActive: ""
  });

  const initialValues = {
    name: "",
    email: "",
    phone: "",
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters"),

    email: Yup.string()
      .required("Email is required")
      .email("Enter a valid email address"),
    phone: Yup.string()
      .required("Phone Number is required")
      .matches(phoneRex, "Enter a Valid Phone Number"),
  });

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ["company_admin"],
    queryFn: () => adminAPI.getAllCompanyAdmins(),
    select: (res) => res.data,
  });


  const activeAdmins = admins?.filter((admin) => admin.is_active === true);
  const inactiveAdmins = admins?.filter((admin) => admin.is_active === false);

  const addAdminMutation = useMutation({
    mutationFn: (adminData) => adminAPI.createCompanyAdmin(adminData),
    onSuccess: () => {
      toast.success("Admin added successfully");
      queryClient.invalidateQueries({ queryKey: ["companyAdmins"] });
      setIsAddModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add admin. Please try again."
      );
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ id, adminData }) => adminAPI.updateCompanyAdmin(id, adminData),
    onSuccess: () => {
      toast.success("Admin updated successfully");
      queryClient.invalidateQueries({ queryKey: ["companyAdmins"] });
      setIsEditModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update admin. Please try again."
      );
    },
  });

  const deleteAdminMutation = useMutation({
    mutationFn: (id) => adminAPI.deleteCompanyAdmin(id),
    onSuccess: () => {
      toast.success("Admin deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["companyAdmins"] });
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete admin. Please try again."
      );
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      mobileNumber: "",
    });
    setCurrentAdmin(null);
  };

  const handleAddAdmin = async (values, { setSubmitting, setFieldError }) => {
    values.password = "Welcome@123";

    try {
      const response = await axios.post(`${API_URL}/admin/create-company-admin`, values, {
        headers: {
          'Content-Type': 'application/json',
          // other headers if required
        }
      });
      if (response.status === 201) {
        toast.success(`${response?.data?.message}`);
        await queryClient.invalidateQueries(["company_admin"]);
        setIsAddModalOpen(false)
      }
    } catch (error) {
      console.error("Registration error:", error);

      // Handle specific field errors from server
      if (error.response?.data?.fieldErrors) {
        Object.keys(error.response.data.fieldErrors).forEach(field => {
          setFieldError(field, error.response.data.fieldErrors[field]);
        });
      } else {
        toast.error(
          error.response?.data?.message || "Registration failed. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      const response = await axios.put(`${API_URL}/admin/update-company-admins/${formData.id}`, formData, {
        headers: {
          authorization: `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        toast.success(`${response?.data?.message}`);
        await queryClient.invalidateQueries(["company_admin"])
        setIsEditModalOpen(false);
      }
    } catch (error) {
      console.error("Registration error:", error);

      // Handle specific field errors from server
      if (error.response?.data?.fieldErrors) {
        Object.keys(error.response.data.fieldErrors).forEach(field => {
          setFieldError(field, error.response.data.fieldErrors[field]);
        });
      } else {
        toast.error(
          error.response?.data?.message || "Registration failed. Please try again."
        );
      }
    } finally {
      //setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(`${API_URL}/admin/delete-company-admins/${currentAdmin._id}`, {
        headers: {
          authorization: `Bearer ${token}`
        }
      });
      if (response.status === 200) {
        toast.success(`${response?.data?.message}`);
        setIsDeleteModalOpen(false);
        await queryClient.invalidateQueries(["company_admin"]);
      }
    } catch (error) {
      console.error("Registration error:", error);

      // Handle specific field errors from server
      if (error.response?.data?.fieldErrors) {
        Object.keys(error.response.data.fieldErrors).forEach(field => {
          setFieldError(field, error.response.data.fieldErrors[field]);
        });
      } else {
        toast.error(
          error.response?.data?.message || "Registration failed. Please try again."
        );
      }
    } finally {
      //setSubmitting(false);
    }

  };

  const openViewModal = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: "", // Don't set the password for security
      mobileNumber: admin.mobileNumber || "",
    });
    setIsViewModalOpen(true);
  }

  const openEditModal = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      id: admin._id,
      name: admin.name,
      email: admin.email,
      password: "", // Don't set the password for security
      mobileNumber: admin.mobileNumber || "",
      isActive: admin.is_active.toString()
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (admin) => {
    setCurrentAdmin(admin);
    setIsDeleteModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Manage Admins</h1>
          <p className="text-muted-foreground">
            Add, edit, or remove company administrators
          </p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Admin
        </Button>
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Active Admins</TabsTrigger>
          <TabsTrigger value="Inactive">InActive Admins</TabsTrigger>
          <TabsTrigger value="performance">Admin Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Company Admins</CardTitle>
            </CardHeader>
            <CardContent>
              {admins.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No admins found. Add your first admin to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>S.No</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeAdmins.map((admin, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell> {/* Serial Number */}
                        <TableCell>{admin.name}</TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>{admin.mobileNumber}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggle(admin._id)}
                              className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${admin.is_active ? 'bg-green-500' : 'bg-red-500'
                                }`}
                            >
                              <div
                                className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${admin.is_active ? 'translate-x-6' : 'translate-x-0'
                                  }`}
                              ></div>
                            </button>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                            >
                              {admin.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => openViewModal(admin)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openEditModal(admin)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="Inactive">
          <Card>
            <CardHeader>
              <CardTitle>Inactive Company Admins List</CardTitle>
            </CardHeader>
            <CardContent>
              {admins.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No admins found. Add your first admin to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>S.No</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveAdmins.map((admin, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell> {/* Serial Number */}
                        <TableCell>{admin.name}</TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>{admin.mobileNumber}</TableCell>
                        <TableCell>
                          {admin.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              In Active
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openViewModal(admin)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openEditModal(admin)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Admin Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Admin Name</TableHead>
                    <TableHead>Exams Created</TableHead>
                    <TableHead>Candidates Managed</TableHead>
                    <TableHead>Active Exams</TableHead>
                    <TableHead>Completion Rate</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin._id}>
                      <TableCell>{admin.name}</TableCell>
                      <TableCell>{admin.stats?.examsCreated || 0}</TableCell>
                      <TableCell>{admin.stats?.candidatesManaged || 0}</TableCell>
                      <TableCell>{admin.stats?.activeExams || 0}</TableCell>
                      <TableCell>
                        {admin.stats?.completionRate
                          ? `${admin.stats.completionRate}%`
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          <ListChecks className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Admin Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Admin</DialogTitle>
          </DialogHeader>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleAddAdmin}
          >{({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Name
                  </Label>
                  <Field
                    as={Input}
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.name && touched.name ? "border-red-500 focus:border-red-500 col-span-3" : "col-span-3"}
                  />
                  <ErrorMessage name="name" component="p" className="text-sm text-red-500 col-span-3 text-center" />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="email" className="text-right">
                    Email
                  </Label>
                  <Field
                    as={Input}
                    id="email"
                    name="email"
                    placeholder="admin@example.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.email && touched.email ? "border-red-500 focus:border-red-500 col-span-3" : "col-span-3"}
                  />
                  <ErrorMessage name="email" component="p" className="text-sm text-red-500 col-span-3 text-center" />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="password" className="text-right">
                    Password
                  </Label>
                  <div className="relative col-span-3">
                    <input
                      as={Input}
                      id="password"
                      name="password"
                      type={"text"}
                      placeholder="Dont Enter password"
                      disabled={true}
                      defaultValue={"Welcome@123"}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={errors.password && touched.password ? "border-red-500 focus:border-red-500 col-span-3" : "col-span-3"}
                    />
                  </div>
                  <ErrorMessage name="password" component="p" className="text-sm text-red-500 col-span-3 text-center" />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="phone" className="text-right">
                    Mobile Number
                  </Label>
                  <Field
                    as={Input}
                    id="phone"
                    name="phone"
                    placeholder="+1 234 567 8901"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.phone && touched.phone ? "border-red-500 focus:border-red-500 col-span-3" : "col-span-3"}
                  />
                  <ErrorMessage name="phone" component="p" className="text-sm text-red-500 col-span-3 text-center" />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={addAdminMutation.isPending || isSubmitting}>
                  {addAdminMutation.isPending ? "Adding..." : "Add Admin"}
                </Button>
              </DialogFooter>
            </Form>
          )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* View Admin Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>View Admin</DialogTitle>
          </DialogHeader>
          <form>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  Name
                </Label>
                <input
                  id="edit-name"
                  style={{ cursor: "not-allowed" }}
                  name="name"
                  value={formData.name}
                  disabled={true}
                  onChange={handleInputChange}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-email" className="text-right">
                  Email
                </Label>
                <input
                  id="edit-email"
                  style={{ cursor: "not-allowed" }}
                  disabled={true}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-mobileNumber" className="text-right">
                  Mobile Number
                </Label>
                <input
                  id="edit-mobileNumber"
                  style={{ cursor: "not-allowed" }}
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  disabled
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="____"
                />
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateAdmin}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  Name
                </Label>
                <Input
                  id="edit-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-email" className="text-right">
                  Email
                </Label>
                <Input
                  id="edit-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="col-span-3"
                  disabled
                  readOnly
                />
              </div>
              {/* <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-password" className="text-right">
                  Password
                </Label>
                <div className="relative col-span-3">
                  <Input
                  id="edit-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Leave to use current password"
                />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={togglePasswordVisibility}
                    >
                    {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                </Button>
                </div>
              </div> */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-mobileNumber" className="text-right">
                  Mobile Number
                </Label>
                <Input
                  id="edit-mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Leave to use current Mobile Number"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-mobileNumber" className="text-right">
                  activeStatus
                </Label>
                <select
                  id="dropdown"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleInputChange}
                  className="border rounded p-2 col-span-3"
                >
                  <option value="true">Active</option>
                  <option value="false">In Active</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateAdminMutation.isPending}>
                {updateAdminMutation.isPending ? "Updating..." : "Update Admin"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Restore Admin Modal */}
      {/* <Dialog open={isEditModalOpen} onOpenChange={setIsRetoreModalOpen}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Restore Admin</DialogTitle>
    </DialogHeader>
   
      <div className="grid gap-4 py-4">
        <div className="text-sm text-gray-700 text-center col-span-4">
          Do you want to restore the user to admin module?
        </div>
      </div>
      <DialogFooter>
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            setIsEditModalOpen(false);
            resetForm();
          }}
        >
          Cancel
        </Button>
        <Button type="submit">
          Restore
        </Button>
      </DialogFooter>
    
  </DialogContent>
</Dialog> */}




      {/* Delete Admin Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to delete the admin{" "}
              <span className="font-semibold">{currentAdmin?.name}</span>? This action cannot
              be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteAdmin}
              disabled={deleteAdminMutation.isPending}
            >
              {deleteAdminMutation.isPending ? "Deleting..." : "Delete Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAdmins;
